// External
import React, { useEffect } from 'react';

// Internal
import {
  features,
  getGlobals,
  getLocale,
  getUnifiedLoginContextData,
  logger,
} from '@/lib';
import {
  isEligibleForApplePay,
  isEligibleForApplePayWithActiveCard,
} from '@paypalcorp/checkout-apple-pay';
import type { ApplePayEligibility } from '@/lib/reactHooks/types';
import { logEligibility } from '@/components/Landing/applepay/fpti';
import { APPLE_PAY_ELIGIBILITY_METRICS } from '../constants';
import type { ApplePayEligibilityResult } from '@paypalcorp/checkout-apple-pay';
import { useStoreState } from '@/store';

export const useApplePay = ({
  applePayButtonSeenOverride = false,
  page,
}: {
  applePayButtonSeenOverride?: boolean; // If true, we ignore the UL Context value, removing it from eligibility checking.
  page?: string;
} = {}): ApplePayEligibility => {
  const applePayButtonEligible = features.isActive('applePayButtonShown');
  const isSntSupported = features.isActive('isSntSupported');

  const isShippingAndTaxLoaded = useStoreState(({ ui }) => {
    return ui.isShippingAndTaxLoaded;
  });

  /**
   * When buyer is eligible for Shipping and Tax flow, we need to wait for the
   * Shipping and Tax Query to be loaded before rendering the button, so all the data
   * we need is cached correctly and avoid the user clicking on the button when
   * that is not ready
   */
  const sntCheckComplete = isSntSupported ? isShippingAndTaxLoaded : true;

  const {
    initialGqlData,
    landingIWCData: { merchantCountry },
  } = getGlobals() || {};

  const { country } = getLocale();
  const { applePayButtonSeen = false } = getUnifiedLoginContextData();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showApplePayButton, setShowApplePayButton] = React.useState(false);
  // Null coalesce to initialGqlData merchant country
  // if landing IWCLandingData merchant country is unavailable
  const merchCountry =
    merchantCountry ?? initialGqlData?.checkoutSession?.merchant?.country;

  const checkApplePayWithActiveCard = async (): Promise<boolean> => {
    const {
      uri: { applePayMerchantIdentifier = null },
    } = getGlobals();
    let applePayEligibilityResult: ApplePayEligibilityResult;
    let isApplePayWithActiveCardEligible = false;

    // A fail-safe if the merchant identifier were to ever
    // be removed from the config, or become invalid for one reason or another
    if (applePayMerchantIdentifier) {
      // Apple Pay Provided Eligibility Check.
      applePayEligibilityResult = await isEligibleForApplePayWithActiveCard({
        buyerCountry: country,
        merchantCountry: merchCountry,
        merchantIdentifier: applePayMerchantIdentifier,
      });

      ({ eligibility: isApplePayWithActiveCardEligible } =
        applePayEligibilityResult);

      logger.info('APPLE_PAY_ACTIVE_CARD_ELIGIBLE', {
        applePayActiveCardEligible: isApplePayWithActiveCardEligible,
      });
    } else {
      applePayEligibilityResult = {
        eligibility: false,
        reason: 'apple_pay_unrecognized_merchant_identifier',
      };

      logger.info('APPLE_PAY_UNRECOGNIZED_MERCHANT_IDENTIFIER', {
        applePayMerchantIdentifier,
      });
    }

    logEligibility({
      applePayButtonEligible,
      applePayButtonSeen,
      applePayButtonSeenOverride,
      applePayEligibilityResult,
      page,
    });

    return isApplePayWithActiveCardEligible;
  };

  useEffect(() => {
    logger.metric(APPLE_PAY_ELIGIBILITY_METRICS.START);

    const applePayEligibilityResult = isEligibleForApplePay({
      buyerCountry: country,
      merchantCountry: merchCountry,
    });
    const { eligibility: isApplePayBrowserEligible } =
      applePayEligibilityResult;

    const shouldCheckForActiveCard =
      (!applePayButtonSeen || applePayButtonSeenOverride) && // Mayfly | Override context checking
      applePayButtonEligible && // Elmo Treatment?
      isApplePayBrowserEligible && // Browser Supports Apple Pay SDK? Ready to go lol.
      sntCheckComplete;

    const getEligibility = async () => {
      try {
        if (shouldCheckForActiveCard) {
          logger.info('APPLE_PAY_ELIGIBLE');
          const showApplePay = await checkApplePayWithActiveCard();

          setShowApplePayButton(showApplePay);
        } else {
          // Added safety.

          setShowApplePayButton(false);
          logEligibility({
            applePayButtonEligible,
            applePayButtonSeen,
            applePayButtonSeenOverride,
            applePayEligibilityResult,
            page,
          });
        }
      } catch (error) {
        logger.metric(APPLE_PAY_ELIGIBILITY_METRICS.ERROR);
        logger.error(APPLE_PAY_ELIGIBILITY_METRICS.ERROR, {
          error_message: error instanceof Error ? error.message : String(error),
        });
      }
    };

    void getEligibility();
  }, [country, sntCheckComplete]);

  return {
    showApplePayButton,
  };
};
